import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "../contentful";
import ProductCard from "./ProductCard";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { CONTENT_TYPES } from "../constants/contentTypes";
import { useLocation } from "react-router-dom";
import ContactModal from "./ContactModal";

// Helper function to preload a single image
const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("//")) return `https:${imageUrl}`;
  return imageUrl;
};

const preloadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      resolve();
      return;
    }
    const img = new Image();
    img.src = normalizeImageUrl(imageUrl);
    img.onload = resolve;
    img.onerror = reject;
  });
};

/**
 * A simple skeleton placeholder grid.
 * You can customize the number of items if desired.
 */
function SkeletonGrid({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-7xl mx-auto">
      {Array.from({ length: count }).map((_, index) => (
        <motion.div
          key={index}
          className="bg-[#F2F2F2] rounded-lg p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Image placeholder */}
          <div className="w-full h-64 bg-[#E5E5E5] rounded-lg mb-4 shimmer" />
          {/* Title placeholder */}
          <div className="h-5 w-3/4 bg-[#E5E5E5] rounded mb-2 shimmer" />
          {/* Subtitle placeholder */}
          <div className="h-4 w-1/2 bg-[#E5E5E5] rounded mb-4 shimmer" />
          {/* Button placeholder */}
          <div className="h-8 w-1/3 bg-[#E5E5E5] rounded-full shimmer" />
        </motion.div>
      ))}
    </div>
  );
}

const ProductsSection = ({ 
  contentType = CONTENT_TYPES.HOUSEHOLD, 
  title = 'Our Products',
  isProfessional = false,
  showQuoteButton = false
}) => {
  const location = useLocation();
  const isProductPage = location.pathname === '/industrial' || 
                       location.pathname === '/household' || 
                       location.pathname === '/household/home' ||
                       location.pathname === '/industrial/home';
  
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [visibleCards, setVisibleCards] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const productsPerPage = isProfessional ? 15 : 6;
  const observerRef = useRef(null);
  const hasAnimatedRef = useRef({});
  const animationQueueRef = useRef([]);
  const isAnimatingRef = useRef(false);

  const processAnimationQueue = () => {
    if (isAnimatingRef.current || animationQueueRef.current.length === 0) return;

    isAnimatingRef.current = true;
    const cardId = animationQueueRef.current[0];

    setTimeout(() => {
      setVisibleCards(prev => ({
        ...prev,
        [cardId]: true
      }));
      hasAnimatedRef.current[cardId] = true;
      animationQueueRef.current.shift();
      isAnimatingRef.current = false;

      // Process next card in queue
      setTimeout(processAnimationQueue, 150);
    }, 10);
  };

  useEffect(() => {
    // Set up intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const cardId = entry.target.dataset.cardId;
          if (entry.isIntersecting && !hasAnimatedRef.current[cardId] && !animationQueueRef.current.includes(cardId)) {
            animationQueueRef.current.push(cardId);
            processAnimationQueue();
          }
        });
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setLoadedImages({});
    setVisibleCards({});
    hasAnimatedRef.current = {};
    animationQueueRef.current = [];
    isAnimatingRef.current = false;
    
    client
      .getEntries({ content_type: contentType })
      .then((response) => {
        setProducts(response.items);
        setIsLoading(false);
        
        // Load images for current page products
        const currentProducts = response.items.slice(
          (currentPage - 1) * productsPerPage,
          currentPage * productsPerPage
        );
        
        currentProducts.forEach((product) => {
          const imageUrl = product.fields.productImage?.fields?.file?.url;
          if (imageUrl) {
            preloadImage(imageUrl)
              .then(() => {
                setLoadedImages(prev => ({
                  ...prev,
                  [product.sys.id]: true
                }));
              })
              .catch((error) => {
                console.error("Error loading image:", error);
                setLoadedImages(prev => ({
                  ...prev,
                  [product.sys.id]: true
                }));
              });
          } else {
            setLoadedImages(prev => ({
              ...prev,
              [product.sys.id]: true
            }));
          }
        });
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setIsLoading(false);
      });
  }, [contentType, currentPage, productsPerPage]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = isProfessional ? products : products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <section id="our-products" className="bg-white py-16 w-full">
      <div className="px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-5xl font-bold text-[#D3242A] mb-12 uppercase tracking-wide text-center"
          style={{ fontFamily: "Geogrotesque, sans-serif" }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {title}
        </motion.h2>

        {showQuoteButton && (
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.button
              type="button"
              onClick={() => setIsContactModalOpen(true)}
              className="inline-block bg-[#D3242A] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#B31E24] transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Request a Quote
            </motion.button>
          </motion.div>
        )}

        <div className="flex justify-center w-full">
          <motion.div 
            className="w-full max-w-[1400px] mx-auto px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.div
              className={`grid gap-4 w-full ${
                isProfessional 
                  ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              }`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <motion.div
                    key={index}
                    className="bg-[#F2F2F2] rounded-lg p-6"
                    variants={itemVariants}
                  >
                    {/* Image placeholder */}
                    <div className="w-full h-64 bg-[#E5E5E5] rounded-lg mb-4 shimmer" />
                    {/* Title placeholder */}
                    <div className="h-5 w-3/4 bg-[#E5E5E5] rounded mb-2 shimmer" />
                    {/* Subtitle placeholder */}
                    <div className="h-4 w-1/2 bg-[#E5E5E5] rounded mb-4 shimmer" />
                    {/* Button placeholder */}
                    <div className="h-8 w-1/3 bg-[#E5E5E5] rounded-full shimmer" />
                  </motion.div>
                ))
              ) : products.length === 0 ? (
                <motion.div 
                  className="text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-black text-lg">No products found!</p>
                </motion.div>
              ) : (
                currentProducts.map((product, index) => {
                  const cardId = product.sys.id || index;
                  const isLoaded = loadedImages[cardId];
                  const isVisible = visibleCards[cardId];

                  return (
                    <div
                      key={cardId}
                      ref={(el) => {
                        if (el && observerRef.current) {
                          observerRef.current.observe(el);
                        }
                      }}
                      data-card-id={cardId}
                      className={`w-full ${isProfessional ? 'max-w-[600px]' : 'max-w-[400px]'} mx-auto`}
                    >
                      {isLoaded && isVisible && (
                        <motion.div
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover={{ 
                            scale: 1.02,
                            transition: { 
                              duration: 0.15,
                              ease: [0.16, 1, 0.3, 1]
                            }
                          }}
                        >
                          <ProductCard 
                            product={product}
                            sequence={index}
                            isProfessional={isProfessional}
                            onRequestQuote={() => setIsContactModalOpen(true)}
                          />
                        </motion.div>
                      )}
                    </div>
                  );
                })
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </section>
  );
};

export default ProductsSection;